import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Home = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="flex items-center space-x-4 mb-8">
                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                  Driving App Growth Through{' '}
                  <span className="text-blue-600">Smart Advertising</span>
                </h1>
              </div>
              <p className="text-xl text-gray-600">
                Scale your app's success with data-driven advertising strategies. We deliver measurable ROI through targeted campaigns and global reach, helping you acquire high-value users.
              </p>
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-600 text-white px-8 py-3 rounded-full font-medium text-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Get Started
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-full font-medium text-lg hover:bg-blue-50 transition-colors duration-300"
                >
                  View Success Stories
                </motion.button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative h-[500px] w-full">
                <img
                  src="/images/marketing.jpg"
                  alt="Marketing Dashboard"
                  className="w-full h-full object-cover rounded-lg shadow-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-sm opacity-80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-aos="fade-up">
              Performance-Driven Solutions
            </h2>
            <p className="text-xl text-gray-600" data-aos="fade-up" data-aos-delay="100">
              Unlock your app's true potential with our comprehensive advertising solutions tailored for growth and engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
                whileHover={{ y: -5 }}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-aos="fade-up">
              Why Choose APPTARGET
            </h2>
            <p className="text-xl text-gray-600" data-aos="fade-up" data-aos-delay="100">
              Partner with us for unparalleled expertise in app marketing and brand growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                className="flex gap-6"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl">
                    {reason.icon}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" data-aos="fade-up">
            Ready to Scale Your App's Growth?
          </h2>
          <p className="text-xl text-white opacity-90 mb-8" data-aos="fade-up" data-aos-delay="100">
            Get in touch with us at contact@apptarget.ai
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-medium text-lg hover:bg-gray-100 transition-colors duration-300"
          >
            Schedule a Consultation
          </motion.button>
        </div>
      </section>
    </div>
  );
};

const features = [
  {
    icon: '📱',
    title: 'App Install Campaigns',
    description: 'Drive quality user acquisition with targeted app install campaigns across premium ad networks and placements. Optimize for high-value users and maximize retention.',
  },
  {
    icon: '🎯',
    title: 'Media Buying & Strategy',
    description: 'Strategic media buying and optimization to maximize your ROI through data-driven decision making. Access premium inventory and exclusive partnerships.',
  },
  {
    icon: '🌟',
    title: 'Influencer Marketing',
    description: 'Connect with relevant influencers to amplify your brand message. Our network spans across platforms, ensuring authentic engagement with your target audience.',
  },
  {
    icon: '📊',
    title: 'Performance Analytics',
    description: 'Real-time campaign tracking with advanced analytics. Get detailed insights into user behavior, attribution, and ROI metrics for data-driven optimization.',
  },
  {
    icon: '🚀',
    title: 'Brand Acceleration',
    description: 'Comprehensive brand growth strategies combining paid and organic channels. Build lasting brand value while driving measurable performance.',
  },
  {
    icon: '🔄',
    title: 'Campaign Optimization',
    description: 'Continuous optimization using AI and machine learning. Adapt campaigns in real-time to maximize performance and reduce user acquisition costs.',
  },
];

const stats = [
  { value: '500+', label: 'Apps Target Driven' },
  { value: '50+', label: 'Countries Reached' },
  { value: '95%', label: 'Client Retention Rate' },
  { value: '200+', label: 'Active Campaigns' },
];

const reasons = [
  {
    icon: '🎯',
    title: 'Industry Expertise',
    description: 'Deep understanding of app marketing landscape and proven strategies that deliver results.',
  },
  {
    icon: '📊',
    title: 'Data-Driven Approach',
    description: 'Advanced analytics and real-time optimization for maximum campaign performance.',
  },
  {
    icon: '🌍',
    title: 'Global Reach',
    description: 'Access to premium advertising networks and partnerships worldwide.',
  },
  {
    icon: '💡',
    title: 'Innovation Focus',
    description: 'Cutting-edge technology and strategies to keep you ahead of the competition.',
  },
];

export default Home;
